import React from 'react'
import {
  IProps,
  SidebarTypes,
  ContentTypes,
  ContentfulSidebarTypes,
  ContentfulContentTypes,
  EditorialWrap,
} from 'marketing-site/src/library/components/EditorialWrap'
import {
  ICustomerWrap as IEditorialWrap,
  ICompanyInfo as IEditorialInfo,
  IRichTextEditor,
  IStatistic,
  IIdLink,
  IImageOrVideoWithCaption,
  ITestimonialTextOnly,
  IEditorialListWithImage,
  ITestimonialLink,
} from 'marketing-site/@types/generated/contentful'
import EntryMarker from 'marketing-site/src/components/common/EntryMarker'
import { transformEditorialInfo } from 'marketing-site/src/transformers/elements/ContentfulEditorialInfo'
import { transformStat } from 'marketing-site/src/transformers/elements/ContentfulStatistic'
import { transformTestimonialTextOnly } from 'marketing-site/src/transformers/components/ContentfulTestimonialTextOnly'
import { transformEditorialListWithImage } from 'marketing-site/src/transformers/components/ContentfulEditorialListWithImage'
import { transformTestimonialLink } from 'marketing-site/src/transformers/components/ContentfulTestimonialLink'
import { transformIDLink } from 'marketing-site/src/transformers/components/ContentfulIDLink'
import { LegacyColor, getHexColorFromName } from 'marketing-site/src/library/utils'

export const ContentfulEditorialWrap = (data: IEditorialWrap) => (
  <EntryMarker entry={data}>
    {() => <EditorialWrap {...transformEditorialWrap(data)} />}
  </EntryMarker>
)

function transformSidebar(item: ContentfulSidebarTypes): SidebarTypes {
  switch (item.sys.contentType.sys.id) {
    case 'companyInfo': {
      return {
        type: 'editorialInfo',
        ...transformEditorialInfo(item as IEditorialInfo),
      }
    }
    case 'statistic': {
      return {
        type: 'statistic',
        ...transformStat(item as IStatistic),
        backgroundColor: LegacyColor.LightGray,
      }
    }

    default: {
      return null
    }
  }
}

function transformContent(item: ContentfulContentTypes): ContentTypes {
  switch (item.sys.contentType.sys.id) {
    case 'richTextEditor': {
      const { fields } = item as IRichTextEditor
      return {
        type: 'Richtext',
        behaviour: fields.behaviour,
        document: fields.richTextEditor,
      }
    }

    case 'imageOrVideoWithCaption': {
      const { fields } = item as IImageOrVideoWithCaption
      return {
        type: 'ImageOrVideo',
        imageRef: {
          url: fields.image.fields.file.url,
          altText: fields.image.fields.title,
        },
        caption: fields.caption,
        video: fields.video?.fields.file.url,
        width: fields.width,
        wistiaId: fields.wistiaId,
      }
    }

    case 'testimonialTextOnly': {
      return {
        type: 'TestimonialTextOnly',
        ...transformTestimonialTextOnly(item as ITestimonialTextOnly),
      }
    }

    case 'editorialListWithImage': {
      return {
        type: 'editorialListWithImage',
        ...transformEditorialListWithImage(item as IEditorialListWithImage),
      }
    }

    case 'testimonialLink': {
      return {
        type: 'TestimonialLink',
        ...transformTestimonialLink(item as ITestimonialLink),
      }
    }

    case 'idLink': {
      return {
        type: 'idLink',
        ...transformIDLink(item as IIdLink),
      }
    }

    default: {
      return null
    }
  }
}

function transformEditorialWrap({ fields }: IEditorialWrap): IProps {
  return {
    ...fields,
    sidebar: fields.sidebar.map(transformSidebar),
    content: fields.content.map(transformContent),
    backgroundColor: fields.backgroundColor
      ? getHexColorFromName(fields.backgroundColor)
      : LegacyColor.White,
  }
}
